import firebase from 'firebase/app';
import LogRocket from 'logrocket';
import { useSnackbar } from "notistack";
import { useCallback, useEffect, useMemo, useState } from "react";
import * as FirestoreHooks from "react-firebase-hooks/firestore";

import { Collections } from "Collections";
import { useAppUser } from "components/AppUserProvider";
import { useOrg, useOrgIdLocalStorage } from "components/OrgProvider";
import { useOrgUser } from "components/OrgUserProvider";
import * as FirebaseConfigs from "FirebaseConfigs";
import { AnalyticsEvent } from "models/AnalyticsEvent";
import { AppUser } from "models/AppUser";
import { EnqueueSnacbar } from "models/EnqueueSnacbar";
import { DbDoc, DbDocFields, DbDocUpdate, WithRef } from 'models/FbDoc';
import { Nullable } from "models/Nullable";
import { OrgUser } from "models/OrgUser";
import { RecursivePartial } from 'models/RecursivePartial';
import { WithoutId } from "models/WithoutId";

export namespace FirebaseUtils {

    function errorMessageFrom(error: firebase.FirebaseError) {
        if (error.code === 'permission-denied') {
            return 'Du har ikke tilgang til dette'
        }
        return error.message
    }

    export function syncLogError(
        appUser: Nullable<AppUser>,
        orgUser: Nullable<OrgUser>,
        enqueueSnackbar: EnqueueSnacbar,
        error: string | firebase.FirebaseError,
        errorEvent: AnalyticsEvent,
        errorEventParams?: Record<string, any>
    ) {
        console.error(error, errorEvent, errorEventParams)
        const message = !(typeof error === 'string') ? errorMessageFrom(error) : error
        LogRocket.captureMessage(message, {
            tags: {
                'syncLogError': true,
            },
            extra: {
                errorEvent,
                ...errorEventParams,
                ...(typeof error === 'string' ? { message } : error),
            },
        })
        enqueueSnackbar(message || 'Noe gikk galt!', { variant: 'error' })
        FirebaseConfigs.analytics.logEvent(errorEvent, {
            ...errorEventParams,
            appUser,
            orgUser,
            error,
            message,
        })
    }

    export function useErrorLogs() {
        const { enqueueSnackbar } = useSnackbar()
        const [org, loadingOrg] = useOrg()
        const [appUser, loadingAppUser] = useAppUser()
        const [orgUser, loadingOrgUser] = useOrgUser()
        const [orgId] = useOrgIdLocalStorage()
        const reportError = useCallback((error: string | firebase.FirebaseError, errorEvent: AnalyticsEvent, errorEventParams?: Record<string, any>) => {
            syncLogError(
                appUser?.data || null,
                orgUser?.data || null,
                enqueueSnackbar,
                error,
                errorEvent,
                {
                    org,
                    orgId,
                    loadingOrg,
                    loadingAppUser,
                    loadingOrgUser,
                    ...errorEventParams,
                },
            )
        }, [
            enqueueSnackbar,
            org,
            appUser,
            orgUser,
            orgId,
            loadingOrg,
            loadingAppUser,
            loadingOrgUser,
        ])

        return [reportError]
    }

    export function useErrorLogger(error: firebase.FirebaseError | undefined, errorEvent: AnalyticsEvent, errorEventParams?: Record<string, any>) {
        const [state] = useState<{ prevError?: firebase.FirebaseError }>({ prevError: undefined })
        const [logError] = useErrorLogs()
        useEffect(() => {
            if (state.prevError !== error) {
                state.prevError = error
                if (error !== undefined) {
                    logError(error, errorEvent, errorEventParams)
                }
            }
        }, [error, state, errorEvent, errorEventParams, logError])
    }


    //    type Join<K, P> = K extends string | number ?
    //    P extends string | number ?
    //    `${K}${"" extends P ? "" : "."}${P}`
    //    : never : never;
    //
    //
    //type Prev = [never, 0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10,
    //    11, 12, 13, 14, 15, 16, 17, 18, 19, 20, ...0[]]
    //
    //type Paths<T, D extends number = 10> = [D] extends [never] ? never : T extends object ?
    //    { [K in keyof T]-?: K extends string | number ?
    //        `${K}` | Join<K, Paths<T[K], Prev[D]>>
    //        : never
    //    }[keyof T] : ""
    //    type QueryWrapper<T> = {
    //        get(): firebase.firestore.Query
    //        where<D extends number = 0>(
    //            fieldPath: Paths<T, D>,
    //            opStr: firebase.firestore.WhereFilterOp,
    //            value: any
    //        ): QueryWrapper<T>
    //        orderBy<D extends number = 0>(
    //            fieldPath: Paths<T, D>,
    //            directionStr?: firebase.firestore.OrderByDirection
    //        ): QueryWrapper<T>
    //        limit(limit: number): QueryWrapper<T>
    //    }

    //    function queryWrapper<T>(queryRefrence: firebase.firestore.CollectionReference<firebase.firestore.DocumentData> | firebase.firestore.Query<firebase.firestore.DocumentData>): QueryWrapper<T> {
    //        return {
    //            get() {
    //                return queryRefrence
    //            },
    //            where(fieldPath, opStr, value) {
    //                return queryWrapper(queryRefrence.where(fieldPath as unknown as string, opStr, value))
    //            },
    //            orderBy(fieldPath, directionStr) {
    //                return queryWrapper(queryRefrence.orderBy(fieldPath as string, directionStr))
    //            },
    //            limit(limit) {
    //                return queryWrapper(queryRefrence.limit(limit))
    //            },
    //        }
    //    }

    type PathSelector<T, C = T> = (C extends {} ? {
        [P in keyof C]: PathSelector<T, C[P]>
    } : C) & {
        getPath(): string
    }

    function pathSelector<T, C = T>(path?: string): PathSelector<T, C> {
        return new Proxy({
            getPath() {
                return path
            },
        } as any, {
            get(target, name: string) {
                if (name === 'getPath') {
                    return target[name]
                }
                return pathSelector(path === undefined ? name : `${path}.${name}` as any)
            }
        })
    }

    type QueryWrapper<T> = {
        getQuery(): firebase.firestore.Query<firebase.firestore.DocumentData>
        where<K>(fieldPathSelector: PathSelector<T, K>, opStr: firebase.firestore.WhereFilterOp, value: Required<K>): QueryWrapper<T>
        orderBy<K>(fieldPathSelector: PathSelector<T, K>, directionStr: firebase.firestore.OrderByDirection): QueryWrapper<T>
        limit(limit: number): QueryWrapper<T>
    }

    function queryWrapper<T>(queryRefrence: firebase.firestore.CollectionReference<firebase.firestore.DocumentData> | firebase.firestore.Query<firebase.firestore.DocumentData>): QueryWrapper<T> {
        return {
            getQuery() {
                return queryRefrence
            },
            where(fieldPathSelector, opStr, value) {
                return queryWrapper(queryRefrence.where(fieldPathSelector.getPath(), opStr, value))
            },
            orderBy(fieldPathSelector, directionStr) {
                return queryWrapper(queryRefrence.orderBy(fieldPathSelector.getPath(), directionStr))
            },
            limit(limit) {
                return queryWrapper(queryRefrence.limit(limit))
            },
        }
    }

    //type SomeObject = {
    //    value: string
    //    otherValue: number
    //    child: SomeObject
    //    otherChild: SomeObject
    //}
    //const path = pathSelector<SomeObject>().child.child.otherChild.child.child.otherValue
    //console.log(path.getPath())
    //function doSomething<T, K>(path: PathSelector<T, K>, value: K){
    //}
    //doSomething(path, 1)
    //doSomething(path, '1')

    export type QueryGetter<T> = (query: QueryWrapper<DbDoc<T>>, pathSelector: PathSelector<DbDoc<T>>) => Nullable<QueryWrapper<T>>

    function moveId<T extends { id: string }>(doc: WithRef<DbDoc<T>>): WithRef<DbDoc<T>> {
        return {
            data: {
                ...doc.data,
                id: (doc as unknown as any)['data.id'],
            },
            metadata: doc.metadata,
            __snap__: doc.__snap__,
        }
    }

    export function useCollectionData<T extends { id: string }>(persist: boolean, collection: Collections | Nullable<firebase.firestore.CollectionReference>, errorEvent: AnalyticsEvent, errorEventParams: Record<string, any>, getQuery: QueryGetter<T>): [value: WithRef<DbDoc<T>>[] | undefined, loading: boolean, error: Nullable<firebase.FirebaseError>] {
        const query = typeof collection === 'string' ? (persist ? FirebaseConfigs.db : FirebaseConfigs.persistedDb).collection(collection) : collection
        const [value, loading, error] = FirestoreHooks.useCollection<DbDoc<T>>((query && getQuery) ? getQuery(queryWrapper(query), pathSelector<DbDoc<T>>())?.getQuery() : query, { snapshotListenOptions: { includeMetadataChanges: true } })
        useErrorLogger(error, errorEvent, errorEventParams)
        return [useMemo(() => value && value.docs.map((doc) => {
            return moveId({
                // @ts-ignore
                'data.id': doc.id,
                data: doc.data().data,
                metadata: doc.data().metadata,
                __snap__: doc,
            })
        }), [value]), loading, error || null]
    }

    export function useDocumentData<T extends { id: string }>(persist: boolean, collection: Collections, documentId: Nullable<string>, errorEvent: AnalyticsEvent, errorEventParams?: Record<string, any>): [value: Nullable<WithRef<DbDoc<T>>>, loading: boolean, error: Nullable<firebase.FirebaseError>] {
        const [value, loading, error] = FirestoreHooks.useDocument<DbDoc<T>>(documentId ? (persist ? FirebaseConfigs.db : FirebaseConfigs.persistedDb).collection(collection).doc(documentId) : null, { snapshotListenOptions: { includeMetadataChanges: true } })
        useErrorLogger(error, errorEvent, errorEventParams)
        const data = useMemo(() => value?.data(), [value])
        return [useMemo(() => (data && value) ? moveId({
            // @ts-ignore
            'data.id': value.id,
            data: data.data,
            metadata: data.metadata,
            __snap__: value,
        }) : null, [value, data]), loading, error || null]
    }

    function createDoc<T extends { id: string }>(ref: firebase.firestore.DocumentReference, data: DbDoc<WithoutId<T>>) {
        return ref.set({
            data: {
                ...data.data,
            },
            metadata: {
                ...data.metadata,
                //createdAt: firebase.firestore.FieldValue.serverTimestamp(),
                //updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
            }
        })
    }
    async function updateDoc<T extends { id: string }>(ref: firebase.firestore.DocumentReference, data: DbDocUpdate<RecursivePartial<WithoutId<T>>>, useSet?: boolean) {
        const payload: DbDocUpdate<RecursivePartial<WithoutId<T>>> = {
            data: data.data,
            metadata: {
                ...(useSet ? (await ref.get()).data()?.metadata as DbDocFields : {}),
                ...data.metadata,
                //updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
            }
        }
        return ref.set(payload, { merge: !useSet })
    }

    function collectionRefFrom(persist: boolean, collection: Collections | firebase.firestore.CollectionReference) {
        return (typeof collection === 'string' ? (persist ? FirebaseConfigs.persistedDb : FirebaseConfigs.db).collection(collection) : collection)
    }

    export function createDocument<T extends { id: string }>(persist: boolean, collection: Collections | firebase.firestore.CollectionReference, docId: Nullable<string>, appUser: Nullable<AppUser>, orgUser: Nullable<OrgUser>, creationEvent: AnalyticsEvent, contextEvent: AnalyticsEvent, errorEvent: AnalyticsEvent, enqueueSnackbar: EnqueueSnacbar, successMessage: Nullable<string>, data: WithoutId<T>, getAnalyticsData?: (ref: firebase.firestore.DocumentReference) => Record<string, any>, callback?: (id: string) => void | Promise<void>) {
        const doc = collectionRefFrom(persist, collection).doc(docId === null ? undefined : docId)
        const { id, ...rest } = data as T
        const toUpload: DbDoc<WithoutId<T>> = {
            data: rest,
            metadata: {
                updatedAt: firebase.firestore.Timestamp.now(),
                createdAt: firebase.firestore.Timestamp.now(),
                updatedBy: {
                    appUser,
                    orgUser,
                },
                createdBy: {
                    appUser,
                    orgUser,
                },
            },
        }

        FirebaseConfigs.analytics.logEvent(creationEvent, {
            appUser,
            orgUser,
            contextEvent: contextEvent,
            ...(getAnalyticsData?.(doc) || {}),
        })
        createDoc<T>(doc, toUpload).catch(error => {
            syncLogError(
                appUser,
                orgUser,
                enqueueSnackbar,
                error,
                errorEvent,
                {
                    contextEvent: contextEvent,
                    ...(getAnalyticsData?.(doc) || {}),
                },
            )
        }).then(() => callback?.(doc.id))
        if (successMessage) {
            enqueueSnackbar(successMessage, { variant: 'success' })
        }
        return doc.id
    }
    export function getDocument<T extends { id: string }>(persist: boolean, collection: Collections, docId: Nullable<string>, appUser: Nullable<AppUser>, orgUser: Nullable<OrgUser>, errorEvent: AnalyticsEvent, enqueueSnackbar: EnqueueSnacbar, getAnalyticsData?: (ref: firebase.firestore.DocumentReference) => Record<string, any>): Promise<Nullable<DbDoc<T>>> {
        return new Promise((resolve, reject) => {
            if (docId === null) {
                resolve(null)
            } else {
                const doc = (persist ? FirebaseConfigs.persistedDb : FirebaseConfigs.db).collection(collection).doc(docId)
                doc.get()
                    .then(ref => {
                        const dbDoc = ref.data()
                        resolve((moveId({
                            ...dbDoc,
                            //@ts-ignore
                            'data.id': doc.id,
                        }) || null) as Nullable<DbDoc<T>>)
                    })
                    .catch(error => {
                        syncLogError(
                            appUser,
                            orgUser,
                            enqueueSnackbar,
                            error,
                            errorEvent,
                            {
                                ...(getAnalyticsData?.(doc) || {}),
                            },
                        )
                        reject(error)
                    })
            }
        })
    }
    export function updateDocument<T extends { id: string }>(persist: boolean, collection: Collections | firebase.firestore.CollectionReference, docId: string, appUser: Nullable<AppUser>, orgUser: Nullable<OrgUser>, updateEvent: AnalyticsEvent, contextEvent: AnalyticsEvent, errorEvent: AnalyticsEvent, enqueueSnackbar: EnqueueSnacbar, successMessage: Nullable<string>, data: RecursivePartial<WithoutId<T>>, getAnalyticsData?: (ref: firebase.firestore.DocumentReference) => Record<string, any>, options?: { useSet?: boolean, anonymousUpdate?: boolean }) {
        const { useSet, anonymousUpdate } = options || {}
        const doc = collectionRefFrom(persist, collection).doc(docId)
        const { id, ...rest } = data as T
        const toUpload: DbDocUpdate<RecursivePartial<WithoutId<T>>> = {
            data: rest,
            metadata: {
                updatedAt: firebase.firestore.Timestamp.now(),
                updatedBy: {
                    appUser: anonymousUpdate ? null : appUser,
                    orgUser: anonymousUpdate ? null : orgUser,
                },
            },
        }

        FirebaseConfigs.analytics.logEvent(updateEvent, {
            appUser,
            orgUser,
            contextEvent: contextEvent,
            ...(getAnalyticsData?.(doc) || {}),
        })
        updateDoc<T>(doc, toUpload, useSet).catch(error => {
            syncLogError(
                appUser,
                orgUser,
                enqueueSnackbar,
                error,
                errorEvent,
                {
                    contextEvent: contextEvent,
                    ...(getAnalyticsData?.(doc) || {}),
                },
            )
        })
        if (successMessage) {
            enqueueSnackbar(successMessage, { variant: 'success' })
        }
        return doc.id
    }
    export function deleteDocument(persist: boolean, collection: Collections | firebase.firestore.CollectionReference, docId: string, appUser: Nullable<AppUser>, orgUser: Nullable<OrgUser>, deleteEvent: AnalyticsEvent, contextEvent: AnalyticsEvent, errorEvent: AnalyticsEvent, enqueueSnackbar: EnqueueSnacbar, successMessage: Nullable<string>, getAnalyticsData?: (ref: firebase.firestore.DocumentReference) => Record<string, any>, callback?: () => void) {
        const doc = (typeof collection === 'string' ? (persist ? FirebaseConfigs.persistedDb : FirebaseConfigs.db).collection(collection) : collection).doc(docId)

        FirebaseConfigs.analytics.logEvent(deleteEvent, {
            appUser,
            orgUser,
            contextEvent: contextEvent,
            ...(getAnalyticsData?.(doc) || {}),
        })
        doc.delete().then(callback).catch(error => {
            syncLogError(
                appUser,
                orgUser,
                enqueueSnackbar,
                error,
                errorEvent,
                {
                    contextEvent: contextEvent,
                    ...(getAnalyticsData?.(doc) || {}),
                },
            )
        })
        if (successMessage) {
            enqueueSnackbar(successMessage, { variant: 'success' })
        }
        return doc.id
    }
    export function deleteDocumentBatch(persist: boolean, collection: Collections, docIds: string[], appUser: Nullable<AppUser>, orgUser: Nullable<OrgUser>, deleteEvent: AnalyticsEvent, contextEvent: AnalyticsEvent, errorEvent: AnalyticsEvent, enqueueSnackbar: EnqueueSnacbar, successMessage: Nullable<string>, getAnalyticsData?: (docIds: string[]) => Record<string, any>) {
        const db = persist ? FirebaseConfigs.persistedDb : FirebaseConfigs.db
        const batch = db.batch()
        docIds.reduce((acc, docId) => {
            const doc = db.collection(collection).doc(docId)
            return acc.delete(doc)
        }, batch)
        batch.commit().catch(error => {
            syncLogError(
                appUser,
                orgUser,
                enqueueSnackbar,
                error,
                errorEvent,
                {
                    contextEvent: contextEvent,
                    ...(getAnalyticsData?.(docIds) || {}),
                },
            )
        })
        FirebaseConfigs.analytics.logEvent(deleteEvent, {
            appUser,
            orgUser,
            contextEvent: contextEvent,
            ...(getAnalyticsData?.(docIds) || {}),
        })
        if (successMessage) {
            enqueueSnackbar(successMessage, { variant: 'success' })
        }
    }
}

